/**
 * Record Flow API - typed wrappers over /api/v1/admin/record-flow, written to
 * docs/record-flow/api-contract.md. Every call resolves (never throws) to
 * { ok: true, data } or { ok: false, error }, so a screen can tell a failed
 * load from an empty one. A failure is never turned into an empty payload.
 */
import { apiRequest } from '../../../services/api';

export type Domain = 'PA' | 'MANIFEST' | 'UMED' | 'FINANCE' | 'COVERAGE' | 'PROVIDER';
export type Kind = 'OPEN' | 'STATUS' | 'OWNER' | 'HANDOFF' | 'DECISION' | 'REVERSAL' | 'CLOSE' | 'REOPEN';
export type Origin = 'PERSON' | 'SYSTEM' | 'IMPORT';

export const KINDS: Kind[] = ['OPEN', 'STATUS', 'OWNER', 'HANDOFF', 'DECISION', 'REVERSAL', 'CLOSE', 'REOPEN'];

export interface FlowWindow { from: string; to: string; timeZone: string }

export interface Step {
  id: string;
  at: string;
  /** false when the source never recorded when this happened; `at` is then only the import instant. */
  timeKnown: boolean;
  /** true when only the calendar date is known (`at` is that date at midnight UTC). */
  dateOnly: boolean;
  domain: Domain;
  recordType: string;
  recordId: string;
  patientId: string | null;
  facilityId: string | null;
  facilityName: string | null;
  kind: Kind;
  fromState: string | null;
  toState: string | null;
  actorUserId: string | null;
  actorName: string | null;
  ownerBeforeUserId: string | null;
  ownerAfterUserId: string | null;
  ownerBeforeName: string | null;
  ownerAfterName: string | null;
  origin: Origin;
  sourceTable: string;
}

export interface Summary {
  window: FlowWindow;
  generatedAt: string;
  kpis: {
    steps: number;
    stepsByOrigin: Record<Origin, number>;
    recordsMoved: number;
    residentsMoved: number;
    activePeople: number;
    cleanupExits: number;
    duplicatesClosed: number;
    sendBacks: number;            // cross-team returns only
    undatedSteps: number;         // steps with no recorded time: a caveat, never plotted
    cleanupExitsImported: number; // PA closes by Monday imports, shown beside cleanupExits, not added in
    reversals: number;            // distinct UMED cases with a reversal or resubmission request
    openWork: { PA: number; UMED: number; FINANCE: number; RESUBMITTALS: number; REPLACEMENTS: number; MANIFEST_UNDECIDED: number; PROVIDER_VISITS: number };
  };
  daily: Array<{ date: string; PA: number; MANIFEST: number; UMED: number; FINANCE: number; COVERAGE: number; PROVIDER: number; PERSON: number; SYSTEM: number; IMPORT: number }>;
  cleanupDaily: Array<{ date: string; word: string; count: number }>;
}

export interface Aging { d0_1: number; d1_3: number; d3_7: number; d7_14: number; d14_30: number; d30p: number }

export interface StageRow {
  domain: Domain;
  stage: string;
  label: string;
  open: number;
  unowned: number;
  /** Open records with no recorded stage-entry time; open = sum(aging) + unaged. */
  unaged: number;
  entered: number;
  exited: number;
  medianDwellHours: number | null;
  p90DwellHours: number | null;
  aging: Aging;
  oldestDays: number | null;
}
export interface Stages { window: FlowWindow; generatedAt: string; stages: StageRow[] }

export interface FlowLink { domain: Domain; from: string; to: string; count: number; medianHoursInFrom: number | null }
export interface Flows { window: FlowWindow; generatedAt: string; links: FlowLink[] }

export interface Person {
  userId: string;
  name: string;
  email: string | null;
  workspaces: string[];
  steps: number;
  byDomain: Partial<Record<Domain, number>>;
  byKind: Partial<Record<Kind, number>>;
  openHeld: number;
  oldestHeldDays: number | null;
  lastStepAt: string | null;
  /** null until owners are recorded on returns. */
  sendBacksReceived: number | null;
  sendBacksMade: number;
}
export interface People {
  window: FlowWindow;
  generatedAt: string;
  people: Person[];
  heat: Array<{ userId: string; date: string; steps: number }>;
}

export type StepRow = Step & { residentLabel: string | null };
export interface StepsPage { window: FlowWindow; generatedAt: string; rows: StepRow[]; nextCursor: string | null }

export interface CurrentState {
  domain: Domain; recordType: string; recordId: string; state: string;
  ownerUserId: string | null; ownerName: string | null; since: string | null;
  open: boolean;
}
export interface Timeline {
  generatedAt: string;
  resident: { id: string; label: string; facilityId: string | null; facilityName: string | null };
  steps: Step[];
  current: CurrentState[];
}

export type AttentionCode = 'NOT_LINKED' | 'NOT_SIGNED_IN_YET' | 'LOGIN_UNUSABLE' | 'LINK_NOT_RATIFIED' | 'EMPLOYEE_INACTIVE'
  | 'NO_ACTIVE_CREDENTIAL' | 'NO_COVERAGE' | 'ACCESS_MISSING' | 'COVERED_BUILDING_CLOSED' | 'VISIT_AT_CLOSED_BUILDING'
  | 'OVERDUE_VISITS' | 'SETUP_RESTARTED' | 'LOGIN_SHARED';

export interface ProviderStop {
  position: number;
  planId: string;
  facilityId: string;
  facilityName: string | null;
  addressLine: string | null;
  mapUrl: string | null;
  /** HH:MM America/New_York, when Scheduling set one. */
  arrivalTime: string | null;
  routeOrder: number | null;
  state: 'PLANNED' | 'CONFIRMED' | 'COMPLETED';
  /** Closed for providers. */
  buildingClosed: boolean;
  /** The provider's first check-in at this building today, ISO UTC. */
  checkedInAt: string | null;
}

export interface ProviderRow {
  employeeId: string;
  name: string;
  employeeActive: boolean;
  credentialActive: boolean;
  login: {
    linked: boolean;
    userId: string | null;
    state: 'NOT_LINKED' | 'ACTIVE' | 'NOT_ACTIVE_YET' | 'DEACTIVATED' | 'SUSPENDED' | 'DELETED' | 'NOT_WORKFORCE';
    activationState: string | null;
    /** From the recorded history: LOGIN_UNUSABLE is a deactivated/suspended/deleted login, never "not signed in yet". */
    signIn: 'NOT_LINKED' | 'NOT_SIGNED_IN_YET' | 'SIGNED_IN' | 'LOGIN_UNUSABLE';
    /** users.last_login_at as an instant; null = never signed in. */
    lastSeenAt: string | null;
    link: 'MANAGER_LINKED' | 'PRE_RULING_LINK' | null;
  };
  coverage: { buildings: number; closed: number; accessInEffect: number; accessMissing: number };
  window: {
    visitsPlanned: number; visitsCompleted: number; visitsCancelled: number; checkIns: number;
    manifestLines: number; manifestDecided: number; manifestUndecided: number; steps: number;
  };
  open: { visits: number; overdue: number; manifestUndecided: number };
  lastActionAt: string | null;
  lastActionSource: 'CHECK_IN' | 'RECORD_FLOW_STEP' | null;
  attention: Array<{ code: AttentionCode; words: string; facilities: Array<{ id: string; name: string | null }> }>;
  today: { date: string; order: 'ROUTE_ORDER_THEN_TIME_THEN_NAME'; stops: ProviderStop[] };
}

export interface Providers {
  window: FlowWindow;
  generatedAt: string;
  today: string;
  providers: ProviderRow[];
  totals: { providers: number; needAttention: number; notSignedInYet: number; stopsToday: number; manifestLinesNoProvider: number };
  notRecorded: Array<{ code: string; words: string }>;
}

export type FlowResult<T> = { ok: true; data: T } | { ok: false; error: string; status?: number; code?: string };

export interface WindowParams { from: string; to: string }
export interface StepsQuery {
  domain?: string; kind?: string; origin?: string; userId?: string; stage?: string;
  patientId?: string; facilityId?: string; limit?: number; cursor?: string | null;
}

/** Administrators reach Record Flow here (admin console). */
export const ADMIN_FLOW_BASE = '/admin/record-flow';
/** Managers who hold RECORD_FLOW_VIEW reach the same shapes here (/portal/record-flow). */
export const PORTAL_FLOW_BASE = '/record-flow';

/**
 * Which base the views read from. Admin unless the portal page says otherwise.
 *
 * A module setting rather than a prop threaded through every view: the five
 * views and the timeline drawer all call `flowApi`, only one Record Flow is
 * ever mounted, and `RecordFlowPortal` sets this in a layout effect (which runs
 * before any view's load effect) and restores it on unmount, so the admin page
 * is unchanged.
 */
let activeBase = ADMIN_FLOW_BASE;
export function setFlowBase(base: string): string {
  const previous = activeBase;
  activeBase = base;
  return previous;
}
export const getFlowBase = (): string => activeBase;

function qs(params: Record<string, string | number | null | undefined>): string {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * One sentence per failure class. "Could not reach the server" and "the
 * server failed" are different facts from "no records", and neither is a
 * permissions problem unless the server said so.
 */
export function describeFailure(r: { status?: number; code?: string; message?: string; error?: string }, base: string = ADMIN_FLOW_BASE): string {
  if (r.status === undefined) return 'Could not reach the server, so nothing below is current.';
  if (r.code === 'WINDOW_TOO_WIDE') return 'That window is wider than 180 days. Choose a shorter one.';
  if (r.status === 404) return 'That record was not found.';
  if (r.status === 401 || r.status === 403) {
    return base === ADMIN_FLOW_BASE
      ? 'You do not have access to Record Flow (it needs an administrator with PHI access).'
      : 'You do not have access to Record Flow (it needs the Record Flow permission and PHI access).';
  }
  if (r.status >= 500) return 'The server failed while assembling this view. This is not an empty result and not a permissions problem.';
  return r.message || r.error || 'Record Flow could not be loaded.';
}

async function get<T>(path: string, params: Record<string, string | number | null | undefined> = {}): Promise<FlowResult<T>> {
  const base = activeBase;
  const r = await apiRequest<T>('GET', `${base}${path}${qs(params)}`);
  if (r.success && r.data) return { ok: true, data: r.data };
  if (r.success) return { ok: false, error: 'The server answered without any data, so nothing below is current.', status: r.status };
  return { ok: false, error: describeFailure(r, base), status: r.status, code: r.code };
}

export const flowApi = {
  summary: (w: WindowParams) => get<Summary>('/summary', { from: w.from, to: w.to }),
  stages: (w: WindowParams) => get<Stages>('/stages', { from: w.from, to: w.to }),
  flows: (w: WindowParams, origin?: string) => get<Flows>('/flows', { from: w.from, to: w.to, origin }),
  people: (w: WindowParams) => get<People>('/people', { from: w.from, to: w.to }),
  steps: (w: WindowParams, q: StepsQuery) => get<StepsPage>('/steps', { from: w.from, to: w.to, limit: 50, ...q }),
  providers: (w: WindowParams) => get<Providers>('/providers', { from: w.from, to: w.to }),
  timeline: (patientId: string) => get<Timeline>(`/residents/${encodeURIComponent(patientId)}/timeline`),
};
